// Flexible registration import engine. Takes ANY standard layout — Excel
// (.xlsx), Google-Sheets exports (.xlsx/.csv), CSV, TSV, or a pasted block — and
// normalizes it to clean rows { teamName, players:[p1,p2], email?, phone?,
// handicap?/playerHandicaps? }, the same shape bulkAddRegistrations already
// consumes. The admin never converts their file; the program figures out the
// columns, and asks only when it genuinely can't tell.
//
// Everything here is grid-based (rows of string cells). readSheetFile turns a
// File into that grid via SheetJS (loaded on demand); pasteToGrid does the same
// for a pasted block; detectTeams does the column detection + row building.

import { buildPlayerHandicaps, parseMemberFlag } from './registrationParse.js'

// Two players inside ONE cell may be separated by a slash (any width), tab,
// ampersand, or the word "and".
const COMBINED_SPLIT = /\s*(?:\t|\/|∕|⁄|／|&|\s+and\s+)\s*/i
const COMBINED_TEST = /[\t/∕⁄／&]|\sand\s/i

const clean = (s) => String(s ?? '').trim()
const isEmail = (s) => /@/.test(s)
const isNumericStr = (s) => s !== '' && Number.isFinite(Number(s))
const looksLikeName = (s) => !!s && !isEmail(s) && !isNumericStr(s) && /[A-Za-z]/.test(s)
const hasCombinedSep = (s) => COMBINED_TEST.test(s)

// A, B, … Z, AA, AB … for headerless column labels.
function colLabel(i) {
  let n = i
  let s = ''
  do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1 } while (n >= 0)
  return s
}

// ── File → grid (SheetJS, lazy-loaded so it never bloats the main bundle) ──────
export async function readSheetFile(file) {
  const XLSX = await import('xlsx')
  const buf = await file.arrayBuffer()
  const wb = XLSX.read(buf, { type: 'array' }) // auto-detects xlsx / csv / tsv
  const sheet = wb.Sheets[wb.SheetNames[0]]
  if (!sheet) return []
  const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: '' })
  return grid.map((r) => (Array.isArray(r) ? r.map(clean) : [])).filter((r) => r.some((c) => c !== ''))
}

// ── Pasted block → grid ───────────────────────────────────────────────────────
export function pasteToGrid(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+$/, ''))
    .filter((l) => l.trim() !== '')
    .map((line) => {
      if (line.includes('\t')) return line.split('\t').map(clean) // Excel/Sheets copy
      if (line.includes(',')) return line.split(',').map(clean)   // CSV-ish paste
      return [clean(line)]                                        // one cell, may hold "P1 / P2"
    })
}

// ── Column detection ──────────────────────────────────────────────────────────
function autoMap(headerRow, dataRows, numCols) {
  const findH = (re) => (headerRow ? headerRow.findIndex((h) => re.test(h)) : -1)
  if (headerRow) {
    const p1 = findH(/player\s*1|p1\b|^player$|name\s*1/i)
    const p2 = findH(/player\s*2|p2\b|name\s*2|partner/i)
    const team = findH(/team\s*name|^team$/i)
    const email = findH(/email/i)
    const phone = findH(/phone/i)
    const hcp1 = findH(/(handicap|hcp|index)\s*1|player\s*1.*(handicap|hcp|index)/i)
    const hcp2 = findH(/(handicap|hcp|index)\s*2|player\s*2.*(handicap|hcp|index)/i)
    const hcpTeam = findH(/^(team\s*)?(handicap|hcp)$/i)
    const member = findH(/^member$|is\s*member|membership/i)
    if (p1 >= 0 && p2 >= 0) {
      return { mode: 'two-col', p1, p2, team: idx(team), email: idx(email), phone: idx(phone), hcp1: idx(hcp1), hcp2: idx(hcp2), hcpTeam: idx(hcpTeam), member: idx(member) }
    }
    const combinedHeader = headerRow.findIndex((h, i) => /player|team|names?/i.test(h) && dataRows.some((r) => hasCombinedSep(clean(r[i]))))
    if (combinedHeader >= 0) {
      return { mode: 'combined', combinedCol: combinedHeader, team: idx(team !== combinedHeader ? team : -1), email: idx(email), phone: idx(phone), member: idx(member) }
    }
  }
  return analyzeColumns(dataRows, numCols)
}

const idx = (i) => (i != null && i >= 0 ? i : null)

function analyzeColumns(dataRows, numCols) {
  const stats = Array.from({ length: numCols }, (_, i) => {
    const cells = dataRows.map((r) => clean(r[i])).filter(Boolean)
    return {
      i,
      total: cells.length,
      nameCount: cells.filter(looksLikeName).length,
      sepCount: cells.filter(hasCombinedSep).length,
      emailCount: cells.filter(isEmail).length,
    }
  })
  const emailCol = stats.find((s) => s.total > 0 && s.emailCount >= Math.ceil(s.total * 0.6))
  // A single column holding "P1 / P2" in most rows → combined.
  const combined = stats.find((s) => s.total > 0 && s.sepCount >= Math.ceil(s.total * 0.6))
  if (combined) return { mode: 'combined', combinedCol: combined.i, team: null, email: emailCol ? emailCol.i : null, phone: null }
  // Exactly two name-heavy columns → the two players. 3+ is genuinely ambiguous.
  const nameCols = stats.filter((s) => s.total > 0 && s.nameCount >= Math.ceil(s.total * 0.6))
  if (nameCols.length === 2) {
    const two = nameCols.slice().sort((a, b) => a.i - b.i)
    return { mode: 'two-col', p1: two[0].i, p2: two[1].i, team: null, email: emailCol ? emailCol.i : null, phone: null }
  }
  return null // ambiguous → caller shows a mapping step
}

function buildRows(dataRows, m, ctx) {
  const out = []
  for (const r of dataRows) {
    let p1 = ''
    let p2 = ''
    if (m.mode === 'combined') {
      const parts = clean(r[m.combinedCol]).split(COMBINED_SPLIT).map(clean).filter(Boolean)
      p1 = parts[0] || ''
      p2 = parts[1] || ''
    } else {
      p1 = clean(r[m.p1])
      p2 = clean(r[m.p2])
    }
    if (!p1 && !p2) continue
    const players = [p1, p2].filter(Boolean)
    const teamName = (m.team != null && clean(r[m.team]))
      ? clean(r[m.team])
      : (players.length === 2 ? `${players[0]} / ${players[1]}` : players[0])
    const row = { teamName, players }
    if (m.email != null && clean(r[m.email])) row.email = clean(r[m.email])
    if (m.phone != null && clean(r[m.phone])) row.phone = clean(r[m.phone])
    if (m.member != null) row.member = parseMemberFlag(r[m.member])
    if (m.hcp1 != null || m.hcp2 != null) {
      const ph = buildPlayerHandicaps([m.hcp1 != null ? clean(r[m.hcp1]) : '', m.hcp2 != null ? clean(r[m.hcp2]) : ''], ctx)
      if (ph) row.playerHandicaps = ph
    } else if (m.hcpTeam != null && clean(r[m.hcpTeam])) {
      const h = Number(clean(r[m.hcpTeam]))
      if (Number.isFinite(h)) row.handicap = h
    }
    out.push(row)
  }
  return out
}

// Detect players + build teams. `override` (a mapping) skips auto-detection —
// used after the admin confirms columns in the mapping step. Returns:
//   { rows, ambiguous, columns, mapping, hasHeader }
export function detectTeams(grid, override = null, ctx = {}) {
  const rows2d = (grid || []).filter((r) => Array.isArray(r) && r.some((c) => clean(c) !== ''))
  if (rows2d.length === 0) return { rows: [], ambiguous: false, columns: [], mapping: null, hasHeader: false }

  const numCols = Math.max(...rows2d.map((r) => r.length))
  const first = rows2d[0].map(clean)
  const autoHeader = first.some((c) => /team|player|name|email|phone|handicap|hcp|index|partner|member/i.test(c))
  // When the admin confirms a manual mapping, they also say whether row 1 is a
  // header (override.hasHeader); otherwise we auto-detect it.
  const hasHeader = override ? !!override.hasHeader : autoHeader
  const headerRow = hasHeader ? first : null
  const dataRows = hasHeader ? rows2d.slice(1) : rows2d

  const columns = Array.from({ length: numCols }, (_, i) => ({
    index: i,
    label: headerRow && headerRow[i] ? headerRow[i] : `Column ${colLabel(i)}`,
    sample: dataRows.slice(0, 3).map((r) => clean(r[i])).filter(Boolean).join(', '),
  }))

  const mapping = override || autoMap(headerRow, dataRows, numCols)
  const usable = mapping && (
    (mapping.mode === 'two-col' && mapping.p1 != null && mapping.p2 != null) ||
    (mapping.mode === 'combined' && mapping.combinedCol != null)
  )
  if (!usable) return { rows: [], ambiguous: true, columns, mapping: mapping || {}, hasHeader, autoHeader }

  return { rows: buildRows(dataRows, mapping, ctx), ambiguous: false, columns, mapping, hasHeader, autoHeader }
}
